import type { CardShape, ProposalDecision, RefView } from "@minga/protocol"
import { createContext } from "react"

/**
 * Minga entity refs in markdown (decision 0029, proposal §10.2): items (`DIC-12` with the project key, and the old `T-n`,
 * `I-n`, `C-n`), goals `G-n`, KPIs `K-n`, proposals `P-n`, runs `R-n`, the temporary teammates `W-n` / `V-n`,
 * outputs `O-n` (wave K), memory `M-n` and decisions `D-n` (decision 0144). The markdown chunk's remark plugin
 * (`remark-refs.ts`) turns refs in prose text nodes into `<minga-ref>` elements, never inside links, code or raw HTML
 * code; `<Markdown>` renders them as chips. Which refs exist (and what a click does) comes from the surface through
 * {@link RefContext}; an unresolved ref stays plain text. This file is in the main chunk (no Streamdown), so surfaces
 * can provide the context without loading the markdown chunk.
 */

export type RefTone = "brand" | "gate" | "running"

export interface ResolvedRef {
  readonly tone: RefTone
  /** Tooltip, e.g. the item title. */
  readonly title?: string | undefined
  /** Opens the ref in place (a sheet over the page, the item page through the router). */
  readonly open?: (() => void) | undefined
  /**
   * Decision 0144: what the chip shows (type icon, status mark, title) and its preview and popover (facts, a card's
   * shape). Absent: a plain chip as before 0144.
   */
  readonly view?: RefView | undefined
}

export type RefResolver = (ref: string) => ResolvedRef | null

export const RefContext = createContext<RefResolver | null>(null)

/**
 * Decision 0144: how a card in a chip's popover, a brief entry or a bell row is decided where it is read. Inside a
 * project, the project socket (`dispatch`, the same command as Needs you); on Home and in the bell, the card route.
 * Resolves to the card's new shape when known, rejects with the reason in words.
 */
export type RefDecide = (card: CardShape, decision: ProposalDecision) => Promise<CardShape | null>
export const RefDecideContext = createContext<RefDecide | null>(null)

/**
 * Decision 0144: the project a chip's refs belong to, for the one lookup a chip makes when the page's resolver doesn't
 * know its ref (a decision comment's D-n, an old run). Null: no lookup.
 */
export const RefLookupContext = createContext<{
  readonly workspaceId: string
  readonly projectId: string
} | null>(null)

/**
 * The one ref pattern and splitter (wave K §4.2: core `refs-text.ts`, `O-n` outputs included), re-exported so the
 * remark plugin and the chips stay unchanged.
 */
export { REF_PATTERN, type RefPiece, splitRefs } from "@minga/core"

/** The tone of a ref by its letter: items, goals, KPIs and outputs brand, proposals gate, runs and teammates running. */
export const refTone = (ref: string): RefTone =>
  ref.startsWith("P-") ? "gate" : /^[RWV]-/.test(ref) ? "running" : "brand"
